import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { new_student_blog_1 } from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower";

export default function Blog22() {

  return (
    <>
     <Fullpage>
      <PageBackground>
        <BlogContainer>
          <BlogWrapper>
            <BlogHeader
              title="AI Is Changing Everything. Are You Ready?"
              date="09 April 2026"
              author="posted by campus insights"
            />
            <BlogContent />
          </BlogWrapper>
        </BlogContainer>
        <OtherBlogs currentBlogId={22} type="ai-lms-students" />
     </PageBackground>
     </Fullpage>
     <CTASection />
    </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  const navigate = useNavigate();
  const handleBackClick = () => {
    window.scrollTo(0, 0);
    navigate("/blog");
  };
  return (
    <HeaderContainer>
      <HeaderContent>
        <BackButtonWrapper>
          <BackButton onClick={handleBackClick} />
        </BackButtonWrapper>
        <MainTitle>{title}</MainTitle>
        <MetadataContainer>
          <MetadataWrapper>
            <MetadataContent>
              <DateText>{date}</DateText>
              <AuthorText>{author}</AuthorText>
            </MetadataContent>
          </MetadataWrapper>
        </MetadataContainer>
      </HeaderContent>
      <HeroImage src={new_student_blog_1} />
      <IntroText>
        Artificial intelligence isn't just another technological trend—it's a fundamental shift in how the world works, learns, and earns. For students today, the question isn't whether AI will affect your future. It already is. The real question is: are you positioning yourself to thrive in it?
      </IntroText>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <BlogSection title="Learning That Mirrors the Real World">
        <ContentText>
          The workplace you'll enter has already been transformed by AI. Employers across every sector—from healthcare to finance to the arts—are looking for graduates who don't just understand their discipline but can apply it alongside intelligent tools. An AI-powered Learning Management System bridges that gap by embedding real-world, tech-forward practices directly into your coursework. You're not just reading about the future; you're practicing it.
        </ContentText>
        <ContentText>
          When your learning environment uses AI to personalize content, flag knowledge gaps, and adapt to your pace, you gain a model for how AI can serve human goals without replacing human judgment. That's exactly the mindset modern employers value most.
        </ContentText>
      </BlogSection>

      <BlogSection title="Courses That Build Future-Proof Skills">
        <ContentText>
          The LXP (Learning eXperience Platform) Student Portal offers a curated library of courses designed specifically to equip students for an AI-integrated world. These aren't abstract electives—they're practical, skill-based programs built for the careers of tomorrow:
        </ContentText>
        <BulletList>
          <BulletItem>AI Literacy and Prompt Engineering</BulletItem>
          <BulletItem>Data Interpretation and Analytics Fundamentals</BulletItem>
          <BulletItem>Digital Communication and Collaboration in Remote Environments</BulletItem>
          <BulletItem>Critical Thinking in an Automated Age</BulletItem>
          <BulletItem>Introduction to Machine Learning Concepts for Non-Engineers</BulletItem>
          <BulletItem>Ethical Technology Use and Responsible AI</BulletItem>
        </BulletList>
        <ContentText>
          Each course is delivered with AI-assisted feedback, real-time progress tracking, and instant certification upon completion—so you can demonstrate your skills with credentials that are ready the moment you finish.
        </ContentText>
      </BlogSection>

      <BlogSection title="Mitigating Job Displacement Through Adaptability">
        <ContentText>
          Yes, AI is automating certain tasks—and some roles will change significantly. But history shows that technological disruption creates as many opportunities as it removes, provided people are prepared. The students who will thrive are those who learn to work with AI, not against it. The portal helps you build that adaptability through:
        </ContentText>
        <BulletList>
          <BulletItem>Continuous learning pathways that update as technology evolves</BulletItem>
          <BulletItem>Cross-disciplinary skill development that broadens your professional value</BulletItem>
          <BulletItem>AI-curated study recommendations that fill in your weakest areas</BulletItem>
          <BulletItem>Collaboration tools that simulate real-world, team-based work environments</BulletItem>
          <BulletItem>Career-readiness modules that connect academic achievements to job-market demands</BulletItem>
        </BulletList>
      </BlogSection>

      <BlogSection title="A Platform Built for the Future">
        <ContentText>
          Campus Insights' LXP Student Portal isn't just a place to submit assignments—it's a dynamic learning environment that grows with you. As AI continues to evolve, so does the platform, ensuring that the skills you build today remain relevant tomorrow. Whether you're a first-year student just finding your footing or a graduating senior preparing to enter the workforce, the portal gives you the tools, knowledge, and confidence to meet the AI era head-on.
        </ContentText>
        <ContentText>
          The future belongs to those who are ready for it. Start preparing today.
        </ContentText>
      </BlogSection>
    </ContentContainer>
  );
};

const BlogSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <SectionContent>{children}</SectionContent>
    </SectionContainer>
  );
};

const BulletList = styled.ul`
  margin: 0;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BulletItem = styled.li`
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
`;

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const SectionTitle = styled.h2`
  align-self: stretch;
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;
  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const ContentText = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MainTitle = styled.h1`
  width: 100%;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 36px;
    line-height: 120%;
  }
  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const HeroImage = styled.img`
  display: flex;
  width: 100%;
  height: 346px;
  justify-content: flex-end;
  align-items: center;
  border-radius: 16px;
  object-fit: cover;
  @media (max-width: 991px) {
    height: 280px;
  }
  @media (max-width: 640px) {
    height: 200px;
  }
`;

const IntroText = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
`;

const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  max-width: 100%;
  box-sizing: border-box;
  background-color: #fff;
  @media (max-width: 991px) {
    width: 100%;
    padding: 40px 32px;
    gap: 48px;
  }
  @media (max-width: 640px) {
    padding: 24px 16px;
    gap: 32px;
  }
`;

const BlogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
  flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 640px) {
    padding: 40px 0;
  }
`;
